import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  Stack,
} from '@mui/material';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';

import startIcon from '../../../assets/images/SVG/start-icon.svg';
import { KpiCard } from '../../common';

const HeadQuestionIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
    <path d="M12 2a7 7 0 0 0-7 7c0 2.38 1.19 4.47 3 5.74V17a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-2.26c1.81-1.27 3-3.36 3-5.74a7 7 0 0 0-7-7z" />
    <path d="M12 7.5c.83 0 1.5.67 1.5 1.5c0 .67-.4 1.1-.8 1.4c-.4.3-.7.6-.7 1.1" />
    <circle cx="12" cy="14.5" r="0.75" fill="currentColor" stroke="none" />
  </svg>
);

const GuidelineBulletIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ff502d" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<\/?[^>]+(>|$)/g, "");
};

export default function BeforeStartSummary({
  examName,
  examDetails,
  exam,
  displayType,
  rawType,
  institute,
  formatDate,
  totalCount,
  guidelines,
  isMixedExam,
  childExams = [],
  mixedTotalQuestions,
  handleOpenStartModal,
  startButtonSx,
  mapChildExamType,
  parseInstructions
}) {
  return (
    <Box sx={{ width: '100%', mt: 1 }}>
      {!isMixedExam ? (
        /* Single Exam Side-by-Side Unified Hero Container Layout */
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 4,
            width: '100%',
            alignItems: 'stretch'
          }}
        >
          {/* Left Card: Exam Details */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, sm: 3, md: 3 },
              border: '1.5px solid #e2e8f0',
              borderRadius: '24px',
              bgcolor: '#ffffff',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.02)',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              boxSizing: 'border-box'
            }}
          >
            <Box>
              <Box sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" gap={1.5} mb={1.5} flexWrap="wrap">
                  <Typography sx={{ fontWeight: 800, fontSize: { xs: '24px', md: '30px' }, color: '#0f172a', fontFamily: 'Poppins', lineHeight: 1.25 }}>
                    {examName}
                  </Typography>
                  <Chip
                    label="Ready to Start"
                    size="small"
                    sx={{
                      bgcolor: 'rgba(16, 185, 129, 0.1)',
                      color: '#10b981',
                      fontWeight: 700,
                      fontSize: '12px',
                      fontFamily: 'Poppins',
                      borderRadius: '10px',
                      px: 1,
                      height: '28px',
                      border: '1px solid rgba(16, 185, 129, 0.2)',
                      flexShrink: 0,
                      ml: 2,
                      mt: 1
                    }}
                  />
                </Stack>
                <Typography sx={{ fontSize: '14.5px', color: '#64748b', fontWeight: 500, fontFamily: 'Poppins' }}>
                  {examDetails?.subjectName || exam.subject} — {exam.topic || examDetails?.gradeName || 'General'}
                </Typography>
              </Box>

              {/* modern Quick Info Cards using Flexbox */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {/* Row 1: Instructor */}
                <Box sx={{ width: '100%' }}>
                  <KpiCard
                    title="Instructor"
                    value={
                      <Box
                        component="span"
                        sx={{
                          display: 'block',
                          fontSize: { xs: '14px', sm: '15px', md: '16px' },
                          fontWeight: 700,
                          color: '#1e293b',
                          fontFamily: 'Poppins',
                          mt: 0.5,
                          lineHeight: 1.2
                        }}
                      >
                        {examDetails?.teacherName || exam.teacher || 'Mr. Rohan Silva'}
                      </Box>
                    }
                    borderColor="#3b82f6"
                    bgGradient="linear-gradient(180deg, rgba(59, 130, 246, 0.08) 0%, rgba(255, 255, 255, 0.15) 100%)"
                  />
                </Box>

                {/* Row 2: Institute, Category, Scheduled Date */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2.5,
                    width: '100%'
                  }}
                >
                  {/* Institute */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <KpiCard
                      title="Institute"
                      value={
                        <Box
                          component="span"
                          sx={{
                            display: 'block',
                            fontSize: { xs: '14px', sm: '15px', md: '16px' },
                            fontWeight: 700,
                            color: '#1e293b',
                            fontFamily: 'Poppins',
                            mt: 0.5,
                            lineHeight: 1.2
                          }}
                        >
                          {examDetails?.institute || institute}
                        </Box>
                      }
                      borderColor="#10b981"
                      bgGradient="linear-gradient(180deg, rgba(16, 185, 129, 0.08) 0%, rgba(255, 255, 255, 0.15) 100%)"
                    />
                  </Box>

                  {/* Category */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <KpiCard
                      title="Category"
                      value={
                        <Box
                          component="span"
                          sx={{
                            display: 'block',
                            fontSize: { xs: '14px', sm: '15px', md: '16px' },
                            fontWeight: 700,
                            color: '#1e293b',
                            fontFamily: 'Poppins',
                            mt: 0.5,
                            lineHeight: 1.2
                          }}
                        >
                          {displayType}
                        </Box>
                      }
                      borderColor="#8b5cf6"
                      bgGradient="linear-gradient(180deg, rgba(139, 92, 246, 0.08) 0%, rgba(255, 255, 255, 0.15) 100%)"
                    />
                  </Box>

                  {/* Scheduled Date */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <KpiCard
                      title="Scheduled Date"
                      value={
                        <Box
                          component="span"
                          sx={{
                            display: 'block',
                            fontSize: { xs: '14px', sm: '15px', md: '16px' },
                            fontWeight: 700,
                            color: '#1e293b',
                            fontFamily: 'Poppins',
                            mt: 0.5,
                            lineHeight: 1.2
                          }}
                        >
                          {formatDate(examDetails?.examDate) || '14th August 2025'}
                        </Box>
                      }
                      borderColor="#f97316"
                      bgGradient="linear-gradient(180deg, rgba(249, 115, 22, 0.08) 0%, rgba(255, 255, 255, 0.15) 100%)"
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* Right Card: Exam Guidelines Section */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, sm: 3, md: 3 },
              border: '1.5px solid #e2e8f0',
              borderRadius: '24px',
              bgcolor: '#ffffff',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.02)',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              boxSizing: 'border-box'
            }}
          >
            <Box>
              {/* Header Box */}
              <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                <Typography sx={{ fontWeight: 800, fontSize: '18px', color: '#0f172a', fontFamily: 'Poppins' }}>
                  {rawType.toUpperCase() === 'MCQ' ? 'Quiz Guidelines' : 'Exam Guidelines'}
                </Typography>
              </Stack>

              {/* Guidelines List */}
              <Stack
                spacing={0.5}
                sx={{
                  mb: 2,
                  maxHeight: { xs: '180px', sm: '210px', md: '240px' },
                  overflowY: 'auto',
                  pr: 1,
                  '&::-webkit-scrollbar': {
                    width: '6px'
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f5f9',
                    borderRadius: '4px'
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#cbd5e1',
                    borderRadius: '4px'
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: '#94a3b8'
                  }
                }}
              >
                {guidelines.map((guideline, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      py: 0.5,
                      px: 1,
                    }}
                  >
                    <Box sx={{ mt: '2px', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                      <GuidelineBulletIcon />
                    </Box>
                    <Typography sx={{ fontSize: '12.5px', color: '#334155', fontFamily: 'Poppins', fontWeight: 555, lineHeight: 1.4 }}>
                      {guideline}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>

            {/* CTA Footer Section */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: 'center',
                borderTop: '1px solid #e2e8f0',
                pt: 1.5,
                gap: 2
              }}
            >
              {/* Quick stats indicators */}
              <Stack direction="row" spacing={1.25} flexWrap="wrap">
                <Chip
                  label={totalCount > 0 ? `${totalCount} Questions` : (rawType.toUpperCase() === 'MCQ' ? '12 Questions' : '15 Questions')}
                  sx={{
                    bgcolor: '#fae8ff',
                    color: '#d946ef',
                    fontWeight: 700,
                    fontSize: '11.5px',
                    fontFamily: 'Poppins',
                    borderRadius: '999px',
                    px: 1.25,
                    height: '28px'
                  }}
                />
                <Chip
                  icon={<AccessTimeOutlinedIcon style={{ color: '#2563eb', fontSize: '14px' }} />}
                  label={examDetails?.duration || exam.duration || '1 H 30 Min'}
                  sx={{
                    bgcolor: '#dbeafe',
                    color: '#2563eb',
                    fontWeight: 700,
                    fontSize: '11.5px',
                    fontFamily: 'Poppins',
                    borderRadius: '999px',
                    px: 1.25,
                    height: '28px',
                    '& .MuiChip-icon': { ml: '2px' }
                  }}
                />
              </Stack>

              {/* Launch Exam CTA Button */}
              <Button
                variant="contained"
                onClick={() => handleOpenStartModal()}
                startIcon={
                  rawType.toUpperCase() === 'MCQ' ? (
                    <Box component="img" src={startIcon} alt="Start" sx={{ width: '16px', height: '16px' }} />
                  ) : (
                    <HeadQuestionIcon />
                  )
                }
                sx={{
                  ...startButtonSx,
                  width: { xs: '100%', sm: 'auto' },
                  px: 3.5,
                  py: 1.5,
                  fontSize: '13px'
                }}
              >
                {rawType.toUpperCase() === 'MCQ' ? 'Start Quiz' : 'Start Exam'}
              </Button>
            </Box>
          </Paper>
        </Box>
      ) : (        /* Mixed Exam Layout */
        <Box
          sx={{
            display: { xs: 'flex', md: 'grid' },
            flexDirection: 'column',
            gridTemplateColumns: '1fr 1fr',
            columnGap: 4,
            rowGap: 3,
            width: '100%',
          }}
        >
          {/* Left Column: Exam Details */}
          <Box sx={{ gridColumn: '1', gridRow: '1 / span 2', height: '100%' }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                height: '100%',
                boxSizing: 'border-box',
                background: 'linear-gradient(135deg, rgba(224, 247, 250, 0.4) 0%, rgba(232, 234, 246, 0.4) 50%, rgba(243, 229, 245, 0.4) 100%)',
                border: '1.5px solid rgba(226, 232, 240, 0.8)',
                borderRadius: '24px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                boxShadow: '0 10px 30px -10px rgba(100, 116, 139, 0.05)'
              }}
            >
              <Box sx={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <Box sx={{ mb: 4 }}>
                  <Stack direction="row" alignItems="center" gap={1.5} mb={1} flexWrap="wrap">
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '20px', md: '24px' }, color: '#0f172a', fontFamily: 'Poppins', lineHeight: 1.25 }}>
                      {examName}
                    </Typography>
                    <Chip
                      label="Ready to Start"
                      size="small"
                      sx={{
                        bgcolor: 'rgba(16, 185, 129, 0.1)',
                        color: '#10b981',
                        fontWeight: 700,
                        fontSize: '11px',
                        fontFamily: 'Poppins',
                        borderRadius: '10px',
                        px: 1,
                        height: '24px',
                        border: '1px solid rgba(16, 185, 129, 0.2)',
                        ml: 2
                      }}
                    />
                  </Stack>
                  <Typography sx={{ fontSize: '13px', color: '#64748b', fontWeight: 550, fontFamily: 'Poppins' }}>
                    {examDetails?.subjectName || exam.subject} — {exam.topic || examDetails?.gradeName || 'General'}
                  </Typography>
                </Box>

                {/* modern Quick Info Cards using Flexbox */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Row 1: Instructor */}
                  <Box sx={{ width: '100%' }}>
                    <KpiCard
                      title="Instructor"
                      value={
                        <Box
                          component="span"
                          sx={{
                            display: 'block',
                            fontSize: { xs: '13px', sm: '14px' },
                            fontWeight: 700,
                            color: '#1e293b',
                            fontFamily: 'Poppins',
                            mt: 0.5,
                            lineHeight: 1.2
                          }}
                        >
                          {examDetails?.teacherName || exam.teacher || 'Mr. Rohan Silva'}
                        </Box>
                      }
                      borderColor="#3b82f6"
                      bgGradient="linear-gradient(180deg, rgba(59, 130, 246, 0.08) 0%, rgba(255, 255, 255, 0.15) 100%)"
                    />
                  </Box>

                  {/* Row 2: Institute, Category, Scheduled Date */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 2,
                      width: '100%'
                    }}
                  >
                    {/* Institute */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <KpiCard
                        title="Institute"
                        value={
                          <Box
                            component="span"
                            sx={{
                              display: 'block',
                              fontSize: { xs: '13px', sm: '14px' },
                              fontWeight: 700,
                              color: '#1e293b',
                              fontFamily: 'Poppins',
                              mt: 0.5,
                              lineHeight: 1.2
                            }}
                          >
                            {examDetails?.institute || institute}
                          </Box>
                        }
                        borderColor="#10b981"
                        bgGradient="linear-gradient(180deg, rgba(16, 185, 129, 0.08) 0%, rgba(255, 255, 255, 0.15) 100%)"
                      />
                    </Box>

                    {/* Category */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <KpiCard
                        title="Category"
                        value={
                          <Box
                            component="span"
                            sx={{
                              display: 'block',
                              fontSize: { xs: '13px', sm: '14px' },
                              fontWeight: 700,
                              color: '#1e293b',
                              fontFamily: 'Poppins',
                              mt: 0.5,
                              lineHeight: 1.2
                            }}
                          >
                            {displayType}
                          </Box>
                        }
                        borderColor="#8b5cf6"
                        bgGradient="linear-gradient(180deg, rgba(139, 92, 246, 0.08) 0%, rgba(255, 255, 255, 0.15) 100%)"
                      />
                    </Box>

                    {/* Scheduled Date */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <KpiCard
                        title="Scheduled Date"
                        value={
                          <Box
                            component="span"
                            sx={{
                              display: 'block',
                              fontSize: { xs: '13px', sm: '14px' },
                              fontWeight: 700,
                              color: '#1e293b',
                              fontFamily: 'Poppins',
                              mt: 0.5,
                              lineHeight: 1.2
                            }}
                          >
                            {formatDate(examDetails?.examDate) || '14th August 2025'}
                          </Box>
                        }
                        borderColor="#f97316"
                        bgGradient="linear-gradient(180deg, rgba(249, 115, 22, 0.08) 0%, rgba(255, 255, 255, 0.15) 100%)"
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Right Column: Sections Guidelines Stacked */}
          {/* Header for sections */}
          <Box sx={{ gridColumn: { md: '2' }, gridRow: { md: '1' }, display: 'flex', alignItems: 'center', gap: 1.5, pl: 0.5, alignSelf: 'end' }}>
            <Typography sx={{ fontWeight: 800, fontSize: '16px', color: '#0f172a', fontFamily: 'Poppins' }}>
              Exam Sections ({childExams.length} sections)
            </Typography>
            <Chip
              label={`${mixedTotalQuestions} Total Questions`}
              size="small"
              sx={{
                bgcolor: '#f3e8ff',
                color: '#9333ea',
                fontWeight: 700,
                fontSize: '11px',
                fontFamily: 'Poppins',
                borderRadius: '999px',
                px: 1
              }}
            />
          </Box>

          {childExams.map((childExam, index) => {
            const childType = mapChildExamType(childExam.examType);
            const childGuidelines = parseInstructions(childExam.instructions);
            const isMcqChild = childType === 'MCQ';

            return (
              <Box key={childExam.examId} sx={{ gridColumn: { md: '2' }, gridRow: { md: `${index + 2}` } }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    boxSizing: 'border-box',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: '24px',
                    bgcolor: '#ffffff',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    boxShadow: '0 10px 30px -10px rgba(100, 116, 139, 0.05)',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      borderColor: '#cbd5e1',
                      boxShadow: '0 15px 35px -10px rgba(100, 116, 139, 0.08)'
                    }
                  }}
                >
                  <Box>
                    {/* Section Info Header */}
                    <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
                      <Typography sx={{ fontWeight: 800, fontSize: '15px', color: '#1e293b', fontFamily: 'Poppins', mr: 'auto' }}>
                        {stripHtml(childExam.examTitle) || (isMcqChild ? 'Quiz Section' : 'Exam Section')}
                      </Typography>
                      <Chip
                        label={`${childExam.examType || ""}`}
                        size="small"
                        sx={{
                          bgcolor: '#ddffdfff',
                          color: '#108800ff',
                          fontWeight: 700,
                          fontSize: '10.5px',
                          fontFamily: 'Poppins',
                          borderRadius: '999px',
                          textTransform: 'capitalize'
                        }}
                      />
                      <Chip
                        label={`${childExam.questionCount || 0} Question`}
                        size="small"
                        sx={{
                          bgcolor: '#fae8ff',
                          color: '#d946ef',
                          fontWeight: 700,
                          fontSize: '10.5px',
                          fontFamily: 'Poppins',
                          borderRadius: '999px'
                        }}
                      />
                      <Chip
                        label={childExam.duration || examDetails?.duration || exam.duration}
                        size="small"
                        sx={{
                          bgcolor: '#dbeafe',
                          color: '#2563eb',
                          fontWeight: 700,
                          fontSize: '10.5px',
                          fontFamily: 'Poppins',
                          borderRadius: '999px'
                        }}
                      />
                    </Box>

                    {/* Section Guidelines */}
                    <Typography sx={{ fontWeight: 700, fontSize: '12px', color: '#64748b', fontFamily: 'Poppins', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Section Guidelines
                    </Typography>

                    <Box
                      sx={{
                        maxHeight: '140px',
                        overflowY: 'auto',
                        pr: 1,
                        '&::-webkit-scrollbar': {
                          width: '4px'
                        },
                        '&::-webkit-scrollbar-track': {
                          background: '#f8fafc',
                          borderRadius: '2px'
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: '#cbd5e1',
                          borderRadius: '2px'
                        }
                      }}
                    >
                      <Stack spacing={1.25}>
                        {childGuidelines.map((guideline, idx) => (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Box sx={{ mt: '3px', display: 'flex', alignItems: 'center' }}>
                              <GuidelineBulletIcon />
                            </Box>
                            <Typography sx={{ fontSize: '12.5px', color: '#475569', fontFamily: 'Poppins', lineHeight: 1.4, fontWeight: 550 }}>
                              {guideline}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  </Box>

                  {/* Launch Action */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, pt: 1.5, borderTop: '1px solid #f1f5f9' }}>
                    <Button
                      variant="contained"
                      onClick={() => handleOpenStartModal(childExam)}
                      startIcon={
                        isMcqChild ? (
                          <Box component="img" src={startIcon} alt="Start" sx={{ width: '16px', height: '16px' }} />
                        ) : (
                          <HeadQuestionIcon />
                        )
                      }
                      sx={{
                        ...startButtonSx,
                        py: 1,
                        px: 3,
                        fontSize: '12.5px'
                      }}
                    >
                      {isMcqChild ? 'Start Quiz' : 'Start Exam'}
                    </Button>
                  </Box>
                </Paper>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
